import React, { memo, useEffect } from 'react';
import {
  OverlayPopover,
  OverlayTooltip,
  Placement,
  TriggerType,
} from '../../../../../../../shared/design-system/components/overlay';
import { accessibleOnClick } from '../../../../../../../shared/utils';
import { ViewFull } from '../../../../../../../shared/svg/full-view';
import { CallLog } from '../../../../../types/call-logs';
import { Template } from '../../../../../types';
import CopyButton from '../../../../../../../shared/components/copy-button';

/** Popover root class + textarea — scroll inside must not close (capture-phase window/table listeners). */
const TRANSCRIPT_POPOVER_SCROLL_IGNORE =
  '.view-full-transcript-modal-popover, .view-full-transcript-modal__textarea';

interface IProps {
  transcript: string | null;
  original: CallLog | Template;
  setSelectedTemplateId?: (id: number | null) => void;
  selectedTemplateId?: number | null;
}

const TranscriptCell: React.FC<IProps> = memo(
  ({ transcript, original, setSelectedTemplateId, selectedTemplateId }) => {
    useEffect(() => {
      if (selectedTemplateId !== original?.id || !setSelectedTemplateId) return;

      const handleScroll = (event: Event) => {
        const t = event.target;
        if (
          t instanceof Element &&
          t.closest(TRANSCRIPT_POPOVER_SCROLL_IGNORE)
        ) {
          return;
        }
        setSelectedTemplateId(null);
      };

      const tableContainer = document.querySelector('.tanstack-table--wrapper');
      if (tableContainer) {
        tableContainer.addEventListener('scroll', handleScroll, {
          passive: true,
        });
      }
      window.addEventListener('scroll', handleScroll, {
        passive: true,
        capture: true,
      });

      // eslint-disable-next-line consistent-return
      return () => {
        tableContainer?.removeEventListener('scroll', handleScroll);
        window.removeEventListener('scroll', handleScroll, { capture: true });
      };
    }, [selectedTemplateId, original?.id, setSelectedTemplateId]);

    if (original?.isTranscriptionInProgress) {
      return (
        <span className="transcript-cell__text-placeholder">
          Transcription in progress...
        </span>
      );
    }

    if (!transcript) {
      return <span>-</span>;
    }

    return (
      <div className="transcript-cell">
        <span className="transcript-cell__text" title={transcript}>
          {transcript}
        </span>
        <div
          className={`transcript-cell__actions ${
            selectedTemplateId === original?.id ? 'active' : ''
          }`}
          aria-hidden
          {...accessibleOnClick((e) => e.stopPropagation())}
        >
          <OverlayPopover
            show={selectedTemplateId === original?.id}
            onToggle={(isOpen: boolean) =>
              setSelectedTemplateId?.(isOpen ? original?.id : null)
            }
            content={
              <textarea
                className="view-full-transcript-modal__textarea"
                value={transcript}
                readOnly
                rows={12}
              />
            }
            popperConfig={{
              modifiers: [
                {
                  name: 'flip',
                  options: {
                    fallbackPlacements: ['bottom'],
                    allowedAutoPlacements: ['top', 'bottom'],
                  },
                },
              ],
            }}
            placement={Placement.Bottom}
            rootClose
            className="view-full-transcript-modal-popover"
          >
            <OverlayTooltip
              text="View Full Transcript"
              trigger={[TriggerType.Hover, TriggerType.Focus]}
            >
              <span
                className={`transcript-cell__action-icon-full-view ${
                  selectedTemplateId === original?.id ? 'active' : ''
                }`}
                {...accessibleOnClick((e) => {
                  e.stopPropagation();
                  setSelectedTemplateId?.(
                    selectedTemplateId === original?.id ? null : original?.id,
                  );
                })}
                aria-label="View Full Transcript"
              >
                <ViewFull width={16} height={16} />
              </span>
            </OverlayTooltip>
          </OverlayPopover>
          <div {...accessibleOnClick((e) => e.stopPropagation())}>
            <CopyButton
              text={transcript}
              copyTooltipText="Copy"
              resetDelay={1000}
              placement={Placement.Bottom}
              className="voicemail-copy-button-icon"
            />
          </div>
        </div>
      </div>
    );
  },
);

TranscriptCell.displayName = 'TranscriptCell';

export default TranscriptCell;
